'use client'

import { useId, useState, type ReactNode } from 'react'

import {
  audienceMix,
  conversionMaturity,
  LAB_TITLES,
  marginalReturn,
  MATURITY_COUNTS,
  responseRevenue,
  type MeasurementLabKind,
} from '~/components/mdx/shared/measurement/models'

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 2,
})
const money = (value: number): string => currencyFormatter.format(value)
const buttonClass =
  'min-h-11 rounded-lg border border-slate-600 px-3 py-2 text-sm text-slate-200 hover:border-violet-300 hover:bg-violet-950 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-cyan-300'

function Slider({
  label,
  value,
  min,
  max,
  step = 1,
  suffix = '',
  prefix = '',
  hint,
  onChange,
}: {
  label: string
  value: number
  min: number
  max: number
  step?: number
  suffix?: string
  prefix?: string
  hint?: string
  onChange: (value: number) => void
}): JSX.Element {
  const id = useId()
  const format = (n: number): string =>
    `${prefix}${n.toLocaleString('en-US')}${n === 1 && suffix === ' days' ? ' day' : suffix}`
  return (
    <div className="min-w-0">
      <label
        htmlFor={id}
        className="mb-2 flex flex-wrap justify-between gap-2 text-sm text-slate-200"
      >
        <span>{label}</span>
        <span aria-hidden="true" className="font-mono text-cyan-200">
          {format(value)}
        </span>
      </label>
      <input
        id={id}
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        aria-valuetext={format(value)}
        aria-describedby={hint ? `${id}-hint` : undefined}
        onChange={(event) => onChange(Number(event.target.value))}
        className="h-11 w-full cursor-pointer accent-violet-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-cyan-300"
      />
      <div
        aria-hidden="true"
        className="flex justify-between text-xs text-slate-400"
      >
        <span>{format(min)}</span>
        <span>{format(max)}</span>
      </div>
      {hint && (
        <p
          id={`${id}-hint`}
          className="mt-2 text-xs leading-relaxed text-slate-300"
        >
          {hint}
        </p>
      )}
    </div>
  )
}

function Scenarios({
  items,
  onReset,
}: {
  items: { label: string; active: boolean; select: () => void }[]
  onReset: () => void
}): JSX.Element {
  return (
    <div className="mb-6">
      <p className="mb-2 text-xs font-semibold text-slate-300">
        Compare a scenario
      </p>
      <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">
        {items.map(({ label, active, select }) => (
          <button
            key={label}
            type="button"
            aria-pressed={active}
            onClick={select}
            className={`${buttonClass} aria-pressed:border-cyan-300 aria-pressed:bg-cyan-950 aria-pressed:text-cyan-100`}
          >
            {label}
          </button>
        ))}
        <button
          type="button"
          onClick={onReset}
          className={`${buttonClass} sm:ml-auto`}
        >
          Reset example
        </button>
      </div>
    </div>
  )
}

function Outcome({
  title,
  children,
  metrics,
  caution = false,
}: {
  title: string
  children: ReactNode
  metrics: [string, string][]
  caution?: boolean
}): JSX.Element {
  return (
    <div
      className={`my-6 rounded-xl border p-4 sm:p-5 ${caution ? 'border-amber-300/40 bg-amber-950/20' : 'border-cyan-300/40 bg-cyan-950/20'}`}
    >
      <div>
        <p className="mb-1 text-xs text-slate-300">In this example</p>
        <p
          className={`text-xl font-semibold ${caution ? 'text-amber-200' : 'text-cyan-200'}`}
        >
          {title}
        </p>
        <p className="mt-2 text-sm leading-relaxed text-slate-200">
          {children}
        </p>
        <Stats items={metrics} />
      </div>
    </div>
  )
}

function Stats({ items }: { items: [string, string][] }): JSX.Element {
  return (
    <dl className="mt-4 grid grid-cols-2 gap-4 border-t border-slate-600/60 pt-4">
      {/* Metrics have fixed positions; keep live nodes mounted as labels change. */}
      {items.map(([label, value], index) => (
        <div key={index} className="min-w-0">
          <dt className="text-xs text-slate-300">{label}</dt>
          <dd className="mt-1 font-mono text-lg font-semibold text-white">
            <span role="status" aria-label={label}>
              {value}
            </span>
          </dd>
        </div>
      ))}
    </dl>
  )
}

function DataTable({
  caption,
  summary,
  columns,
  rows,
}: {
  caption: string
  summary: string
  columns: string[]
  rows: (string | number)[][]
}): JSX.Element {
  return (
    <details className="mt-6 border-t border-slate-700 pt-4">
      <summary className="min-h-11 cursor-pointer rounded px-1 py-3 text-sm font-medium text-violet-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-cyan-300">
        {summary}
      </summary>
      <div className="mt-2 overflow-x-auto">
        <table className="w-full text-left text-sm text-slate-200">
          <caption className="mb-3 text-left text-xs text-slate-300">
            {caption}
          </caption>
          <thead>
            <tr>
              {columns.map((column) => (
                <th
                  scope="col"
                  key={column}
                  className="border-b border-slate-600 p-2 font-medium"
                >
                  {column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i}>
                {row.map((cell, j) => (
                  <td className="border-b border-slate-800 p-2" key={j}>
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  )
}

function MixLab(): JSX.Element {
  const [aWarm, setAWarm] = useState(80)
  const [bWarm, setBWarm] = useState(20)
  const result = audienceMix(aWarm, bWarm)
  const setMix = (a: number, b: number): void => {
    setAWarm(a)
    setBWarm(b)
  }
  const difference = result.a - result.b
  const title =
    Math.abs(difference) < 0.000001
      ? 'The overall conversion rates tie'
      : `${difference > 0 ? 'A' : 'B'} leads overall by ${Math.abs(difference).toFixed(2)} percentage points`
  const groups = [
    {
      title: 'At the selected audience mix',
      rows: [
        ['A', result.a, '#c4b5fd'],
        ['B', result.b, '#67e8f9'],
      ],
    },
    {
      title: 'At the same 50/50 audience mix',
      rows: [
        ['A', result.aStandard, '#c4b5fd'],
        ['B', result.bStandard, '#67e8f9'],
      ],
    },
  ] satisfies { title: string; rows: [string, number, string][] }[]
  return (
    <>
      <Scenarios
        onReset={() => setMix(80, 20)}
        items={[
          {
            label: 'Different audiences',
            active: aWarm === 80 && bWarm === 20,
            select: () => setMix(80, 20),
          },
          {
            label: 'Equalize delivery',
            active: aWarm === 50 && bWarm === 50,
            select: () => setMix(50, 50),
          },
          {
            label: 'Reverse the mix',
            active: aWarm === 20 && bWarm === 80,
            select: () => setMix(20, 80),
          },
        ]}
      />
      <div className="grid gap-5 sm:grid-cols-2">
        <Slider
          label="A: warm-audience share"
          value={aWarm}
          min={0}
          max={100}
          step={5}
          suffix="%"
          onChange={setAWarm}
        />
        <Slider
          label="B: warm-audience share"
          value={bWarm}
          min={0}
          max={100}
          step={5}
          suffix="%"
          onChange={setBWarm}
        />
      </div>
      <Outcome
        title={title}
        caution={difference > 0}
        metrics={[
          ["A's overall conversion rate", `${result.a.toFixed(2)}%`],
          ["B's overall conversion rate", `${result.b.toFixed(2)}%`],
        ]}
      >
        At a common 50/50 mix, B leads by 0.75 percentage points. Only the
        audience weights change; the segment rates stay fixed.
      </Outcome>
      <div
        role="img"
        aria-label={`Conversion rates. Selected mix: A ${result.a.toFixed(2)} percent, B ${result.b.toFixed(2)} percent. Same 50/50 mix: A 4.50 percent, B 5.25 percent. Both charts run from zero to ten percent.`}
        className="grid gap-5 sm:grid-cols-2"
      >
        {groups.map((group) => (
          <div
            key={group.title}
            className="rounded-xl border border-slate-700 p-4"
          >
            <p className="mb-4 text-sm font-medium text-slate-200">
              {group.title}
            </p>
            {group.rows.map(([label, rate, color]) => (
              <div key={label} className="mb-4">
                <div className="mb-2 flex justify-between text-sm">
                  <span>Creative {label}</span>
                  <span className="font-mono">{rate.toFixed(2)}%</span>
                </div>
                <div className="h-4 rounded bg-slate-800">
                  <div
                    className="h-4 rounded"
                    style={{ width: `${rate * 10}%`, backgroundColor: color }}
                  />
                </div>
              </div>
            ))}
            <div className="flex justify-between text-xs text-slate-400">
              <span>0%</span>
              <span>Conversion rate</span>
              <span>10%</span>
            </div>
          </div>
        ))}
      </div>
      <p className="mt-4 text-xs leading-relaxed text-slate-300">
        Same scale, different weights. A common mix makes the arithmetic
        comparable; it does not prove which creative caused more purchases.
      </p>
      <DataTable
        summary="See the segment rates and click allocation"
        caption="Synthetic rates stay fixed. Each creative receives 1,000 clicks; the sliders change who those clicks come from."
        columns={['Audience', 'A clicks / CVR', 'B clicks / CVR']}
        rows={[
          [
            'Cold',
            `${(100 - aWarm) * 10} / 1.00%`,
            `${(100 - bWarm) * 10} / 1.50%`,
          ],
          ['Warm', `${aWarm * 10} / 8.00%`, `${bWarm * 10} / 9.00%`],
          ['50/50 reference', '500 each / 4.50%', '500 each / 5.25%'],
        ]}
      />
    </>
  )
}

function LagLab(): JSX.Element {
  const [day, setDay] = useState(2)
  const [completeness, setCompleteness] = useState(60)
  const result = conversionMaturity(day, completeness)
  const setAge = (age: number): void => {
    setDay(age)
    setCompleteness(MATURITY_COUNTS[age - 1] ?? 40)
  }
  const mismatch = completeness !== MATURITY_COUNTS[day - 1]
  return (
    <>
      <Scenarios
        onReset={() => setAge(2)}
        items={[
          {
            label: 'Day 2 snapshot',
            active: day === 2 && completeness === 60,
            select: () => setAge(2),
          },
          {
            label: 'Mature cohort',
            active: day === 7 && completeness === 100,
            select: () => setAge(7),
          },
          {
            label: 'Wrong assumption',
            active: day === 2 && completeness === 80,
            select: () => {
              setDay(2)
              setCompleteness(80)
            },
          },
        ]}
      />
      <div className="grid gap-5 sm:grid-cols-2">
        <Slider
          label="Cohort age"
          value={day}
          min={1}
          max={7}
          suffix=" days"
          onChange={setAge}
          hint="Changing age loads the reference completeness for that day."
        />
        <Slider
          label="Assumed historical completeness"
          value={completeness}
          min={40}
          max={100}
          suffix="%"
          onChange={setCompleteness}
          hint="Change this assumption to see how sensitive the estimate is."
        />
      </div>
      <Outcome
        title={
          mismatch
            ? 'Same observations. Different forecast.'
            : day === 7
              ? 'The cohort has reached its $30 CPA'
              : 'CPA can fall without a campaign change'
        }
        caution={mismatch}
        metrics={[
          [`Observed CPA · day ${day}`, money(result.rawCpa)],
          ['Estimated final CPA', money(result.adjustedCpa)],
        ]}
      >
        {result.observed} recorded purchases ÷ {completeness}% assumed
        completeness = {result.projected.toFixed(1)} projected purchases.
        {mismatch
          ? ' This assumption misses the example’s known endpoint: 100 purchases and a $30 CPA.'
          : ' The estimate matches this example’s known endpoint. Real cohorts need a backtested lag curve.'}
      </Outcome>
      <div
        role="img"
        aria-label={`Cumulative purchases by age; selected day ${day}. ${MATURITY_COUNTS.map((n, i) => `Day ${i + 1}: ${n}, ${i + 1 <= day ? 'recorded by selected age' : 'future in this teaching example'}`).join('. ')}. Zero baseline.`}
      >
        <div className="flex h-48 items-end gap-2 border-b border-slate-600">
          {MATURITY_COUNTS.map((n, i) => (
            <div
              key={i}
              className="flex h-full flex-1 flex-col justify-end text-center"
            >
              <span className="mb-2 font-mono text-xs text-slate-200">{n}</span>
              <div
                className={`mx-auto w-full max-w-12 rounded-t ${i + 1 > day ? 'border-2 border-dashed border-slate-400 bg-slate-800/40' : i + 1 === day ? 'bg-cyan-300' : 'bg-violet-400'}`}
                style={{ height: `${n * 1.3}px` }}
              />
              <span
                className={`whitespace-nowrap py-2 text-xs ${i + 1 === day ? 'font-bold text-cyan-200' : 'text-slate-300'}`}
              >
                D{i + 1}
                {i + 1 === day ? '↓' : ''}
              </span>
            </div>
          ))}
        </div>
      </div>
      <p className="mt-3 text-xs leading-relaxed text-slate-300">
        Purchases recorded by cohort age. Filled bars: known by day {day}.
        Dashed bars: future observations, revealed here for teaching. The cyan
        bar marks your selected day.
      </p>
      <DataTable
        summary="Inspect the full seven-day cohort"
        caption="One fixed $3,000 cohort, observed repeatedly. In a live report, later days would not yet be known."
        columns={['Age', 'Purchases', 'Observed CPA']}
        rows={MATURITY_COUNTS.map((n, i) => [
          `Day ${i + 1}${i + 1 > day ? ' (future)' : ''}`,
          n,
          money(3000 / n),
        ])}
      />
    </>
  )
}

const curveX = (spend: number): number => 70 + (spend / 40000) * 305
const curveY = (revenue: number): number => 225 - (revenue / 60000) * 180
const responsePath = Array.from(
  { length: 81 },
  (_, i) =>
    `${i === 0 ? 'M' : 'L'} ${curveX(i * 500)} ${curveY(responseRevenue(i * 500))}`,
).join(' ')

function MarginalLab(): JSX.Element {
  const [spend, setSpend] = useState(10000)
  const [step, setStep] = useState(5000)
  const [margin, setMargin] = useState(60)
  const result = marginalReturn(spend, step, margin)
  const x = curveX
  const y = curveY
  const setScenario = (s: number, increase: number, m: number): void => {
    setSpend(s)
    setStep(increase)
    setMargin(m)
  }
  const breakEven = Math.abs(result.contribution) < 0.005
  const change = breakEven ? 0 : result.contribution
  return (
    <>
      <Scenarios
        onReset={() => setScenario(10000, 5000, 60)}
        items={[
          {
            label: '3× average return',
            active: spend === 10000 && step === 5000 && margin === 60,
            select: () => setScenario(10000, 5000, 60),
          },
          {
            label: 'Lower starting spend',
            active: spend === 1000 && step === 5000 && margin === 60,
            select: () => setScenario(1000, 5000, 60),
          },
          {
            label: 'Higher margin',
            active: spend === 10000 && step === 5000 && margin === 85,
            select: () => setScenario(10000, 5000, 85),
          },
        ]}
      />
      <div className="grid gap-5 sm:grid-cols-3">
        <Slider
          label="Current spend"
          prefix="$"
          value={spend}
          min={1000}
          max={30000}
          step={1000}
          onChange={setSpend}
        />
        <Slider
          label="Proposed increase"
          prefix="$"
          value={step}
          min={1000}
          max={10000}
          step={1000}
          onChange={setStep}
        />
        <Slider
          label="Contribution margin before ads"
          value={margin}
          min={20}
          max={90}
          step={5}
          suffix="%"
          onChange={setMargin}
        />
      </div>
      <Outcome
        title={
          breakEven
            ? 'This increase breaks even'
            : `Contribution ${change > 0 ? 'rises' : 'falls'} by ${money(Math.abs(change))}`
        }
        caution={change < 0}
        metrics={[
          ['Average revenue / spend', `${result.average.toFixed(2)}×`],
          ['Return on the increase', `${result.stepReturn.toFixed(2)}×`],
        ]}
      >
        {money(step)} more spend adds {money(result.extraRevenue)} revenue. At{' '}
        {margin}% margin, that leaves{' '}
        {money((result.extraRevenue * margin) / 100)} before paying for the ads.
        {breakEven
          ? ' It covers the extra spend, with no contribution left over.'
          : ` The increase ${change > 0 ? 'clears' : 'falls below'} the ${result.hurdle.toFixed(2)}× break-even return.`}
      </Outcome>
      <svg
        viewBox="0 0 400 300"
        className="mx-auto my-5 w-full max-w-md [&_text]:text-[24px] sm:[&_text]:text-[16px]"
        role="img"
        aria-label={`Illustrative incremental revenue response. At ${money(spend)} spend revenue is ${money(result.revenue)}; at ${money(spend + step)} spend revenue is ${money(responseRevenue(spend + step))}. Both axes start at zero.`}
      >
        {[0, 20000, 40000, 60000].map((v) => (
          <g key={v}>
            <line x1="70" x2="375" y1={y(v)} y2={y(v)} stroke="#334155" />
            <text
              x="63"
              y={y(v) + 4}
              textAnchor="end"
              fill="#cbd5e1"
              fontSize="16"
            >
              ${v / 1000}k
            </text>
          </g>
        ))}
        <text x="70" y="28" fill="#cbd5e1" fontSize="16">
          Incremental revenue ($)
        </text>
        {[0, 20000, 40000].map((v) => (
          <text
            key={v}
            x={x(v)}
            y="254"
            textAnchor={v === 40000 ? 'end' : 'middle'}
            fill="#cbd5e1"
            fontSize="16"
          >
            ${v / 1000}k
          </text>
        ))}
        <text x="222" y="288" textAnchor="middle" fill="#cbd5e1" fontSize="16">
          Spend ($) · fixed horizon
        </text>
        <path d={responsePath} fill="none" stroke="#c4b5fd" strokeWidth="3" />
        <line
          x1={x(spend)}
          y1={y(result.revenue)}
          x2={x(spend + step)}
          y2={y(responseRevenue(spend + step))}
          stroke="#67e8f9"
          strokeWidth="3"
          strokeDasharray="6 4"
        />
        {[spend, spend + step].map((v, i) => (
          <g key={v}>
            <line
              x1={x(v)}
              x2={x(v)}
              y1={y(responseRevenue(v))}
              y2="225"
              stroke="#94a3b8"
              strokeDasharray="3 4"
            />
            <circle
              cx={x(v)}
              cy={y(responseRevenue(v))}
              r="6"
              fill={i ? '#67e8f9' : '#c4b5fd'}
            />
          </g>
        ))}
      </svg>
      <p className="text-xs text-slate-300">
        Violet: assumed response curve. Dashed cyan segment: the proposed budget
        increase.
      </p>
      <p className="mt-3 text-xs leading-relaxed text-slate-300">
        An assumed curve for a fixed horizon, not an account forecast. Change
        margin to see the decision change while the revenue curve stays put.
      </p>
      <DataTable
        summary="See the budget arithmetic"
        caption="The next-dollar derivative and the finite budget-step return answer different questions."
        columns={['Quantity', 'Value']}
        rows={[
          ['Current incremental revenue', money(result.revenue)],
          ['Revenue after increase', money(responseRevenue(spend + step))],
          ['Extra revenue', money(result.extraRevenue)],
          ['Change in contribution', money(change)],
          ['Local next-dollar return', `${result.marginal.toFixed(2)}×`],
          ['Finite increase return', `${result.stepReturn.toFixed(2)}×`],
          ['Break-even revenue return', `${result.hurdle.toFixed(2)}×`],
        ]}
      />
    </>
  )
}

const LAB_PROMPTS: Record<MeasurementLabKind, string> = {
  mix: 'Can the winning ad change when only its audience changes? Compare delivery mixes, then adjust the shares yourself.',
  lag: 'Did performance improve, or did purchases arrive later? Compare the same cohort at different ages, then challenge the forecast.',
  marginal:
    'Does the next budget increase pay for itself? Compare three scenarios, then change spend or margin to test the economics.',
}

/** Guided teaching scenarios with keyboard controls and inspectable calculations. */
export default function MeasurementDecisionLab({
  kind = 'mix',
}: {
  kind?: MeasurementLabKind
}): JSX.Element {
  return (
    <section
      data-measurement-lab={kind}
      aria-label={LAB_TITLES[kind]}
      className="not-prose my-10 overflow-hidden rounded-2xl border border-violet-400/30 bg-slate-950 p-5 text-slate-100 shadow-xl sm:p-8"
    >
      <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-cyan-200">
        AdSights · interactive example
      </p>
      <h3 className="mb-3 text-2xl font-semibold text-white">
        {LAB_TITLES[kind]}
      </h3>
      <p className="mb-7 text-sm leading-relaxed text-slate-300">
        {LAB_PROMPTS[kind]}
      </p>
      <p className="mb-5 -mt-4 text-xs text-slate-400">
        Synthetic teaching data{kind !== 'mix' ? ' · USD' : ''}
      </p>
      {kind === 'mix' ? (
        <MixLab />
      ) : kind === 'lag' ? (
        <LagLab />
      ) : (
        <MarginalLab />
      )}
    </section>
  )
}
